import { useRegisterUser } from '@netfront/gelada-identity-library';
import { Button, Checkbox, Input, PasswordInput, useToast } from '@netfront/ui-library';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import { getValidClassNames } from '../../../utils';
import * as yup from 'yup';

import { IRegisterFormFields } from './RegisterForm.interfaces';
import Link from 'next/link';
import { PasswordValidator } from '@/components/PasswordValidator/PasswordValidator';
import {
  REG_EXP_MINIMUM_EIGHT_CHARACTERS,
  REG_EXP_NUMBER,
  REG_EXP_ONE_LOWERCASE,
  REG_EXP_ONE_UPPERCASE,
  REG_EXP_SPECIAL_CHARACTER,
} from '@/components/PasswordValidator/PasswordValidator.constants';
import { useToggle } from '@netfront/common-library';
import { CONSTANTS } from '@/constants';

export const RegisterForm = () => {
  const { push } = useRouter();
  const { handleToastError } = useToast();
  const { isToggled: isSubmittingRegister, toggle: toggleIsSubmittingRegister } = useToggle();

  const formik = useFormik<IRegisterFormFields>({
    validateOnBlur: true,
    initialValues: {
      email: '',
      firstName: '',
      hasAcceptedTerms: false,
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: yup.object().shape({
      email: yup.string().email('Please enter a valid email address').required('Please enter in a valid email address'),
      firstName: yup.string().required('Please enter your first name'),
      lastName: yup.string().required('Please enter your last name'),
      password: yup
        .string()
        .required('Please enter in a password')
        .matches(REG_EXP_MINIMUM_EIGHT_CHARACTERS, 'Password must be at least 8 characters')
        .matches(REG_EXP_ONE_LOWERCASE, 'Password must contain at least one lowercase letter')
        .matches(REG_EXP_ONE_UPPERCASE, 'Password must contain at least one uppercase letter')
        .matches(REG_EXP_NUMBER, 'Password must contain at least one number')
        .matches(REG_EXP_SPECIAL_CHARACTER, 'Password must contain at least one special character'),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref('password')], 'Passwords must match')
        .required('Please confirm your password'),
      hasAcceptedTerms: yup.boolean().isTrue('Please read and accept the terms and conditions').required(),
    }),
    onSubmit: ({ email, firstName, lastName, password }: IRegisterFormFields) => {
      handleRegisterUser({
        credential: {
          email,
          password,
        },
        user: {
          firstname: firstName,
          lastname: lastName,
          smartCode: CONSTANTS.GROUPS.CODES.PARTICIPANT.ID,
        },
      });
    },
  });

  const { errors, touched, values, handleChange, handleSubmit, handleBlur } = formik;

  const { handleRegisterUser } = useRegisterUser({
    onCompleted: () => {
      push('/activate-account');
    },
    onError: (error) => {
      toggleIsSubmittingRegister();

      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  return (
    <form className="flex flex-col" onSubmit={handleSubmit}>
      <Input
        errorMessage={touched.firstName && errors.firstName ? errors.firstName : ''}
        id="firstName"
        labelText="First name"
        name="firstName"
        placeholder="Enter your first name"
        type="text"
        value={values.firstName}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <Input
        errorMessage={touched.lastName && errors.lastName ? errors.lastName : ''}
        id="lastName"
        labelText="Last name"
        name="lastName"
        placeholder="Enter your last name"
        type="text"
        value={values.lastName}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <Input
        errorMessage={touched.email && errors.email ? errors.email : ''}
        id="email"
        labelText="Email"
        name="email"
        placeholder="Enter email"
        type="email"
        value={values.email}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <PasswordInput
        errorMessage={touched.password && errors.password ? errors.password : ''}
        id="password"
        labelText="Password"
        name="password"
        placeholder="Enter password"
        value={values.password}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <PasswordInput
        errorMessage={touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ''}
        id="confirmPassword"
        labelText="Repeat password"
        name="confirmPassword"
        placeholder="Enter password"
        value={values.confirmPassword}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <PasswordValidator firstPassword={values.password} secondPassword={values.confirmPassword} />

      <div className="py-4">
        <div>
          <p className="h5 mb-4">Terms of use</p>
          <p className="text-sm mb-4">
            By selecting "I agree", you confirm that you accept our site terms of use and agree to our privacy information. You will receive
            an activation email shortly after you sign up, please follow the simple instructions to verify your profile and get free program
            access.
          </p>
          <label className="flex items-center mb-6" htmlFor="hasAcceptedTerms">
            <Checkbox
              additionalClassNames={`${errors.hasAcceptedTerms ? '!border-red-800' : 'border-accent'}`}
              id="hasAcceptedTerms"
              isChecked={values.hasAcceptedTerms}
              labelText="Terms and conditions"
              name="hasAcceptedTerms"
              value="hasAcceptedTerms"
              isLabelHidden
              onChange={handleChange}
            />

            <p className="ml-3">
              I agree to the{' '}
              <Link href="/terms-and-conditions" className="color-primary weight-600">
                terms and conditions
              </Link>
            </p>
          </label>

          {Boolean(touched.hasAcceptedTerms && errors.hasAcceptedTerms) && (
            <div className="mt-4">
              <span
                className={getValidClassNames({
                  'line-1.2': true,
                  'color-red-400': Boolean(errors.hasAcceptedTerms),
                })}
              >
                {errors.hasAcceptedTerms}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="text-right mb-8">
        <Button
          isLoading={Boolean(!errors) && isSubmittingRegister}
          text="Register"
          type="submit"
          variant="accent"
          onClick={toggleIsSubmittingRegister}
        />
      </div>

      <p>
        Already have an account?{' '}
        <Link href="/login" className="color-primary weight-600">
          Login
        </Link>
      </p>
    </form>
  );
};
